// ── COACHING: the assignment board ───────────────────────────────────
// Where Lori solves the puzzle. Columns are coaches, rows are 20-minute slots,
// and the left rail holds everyone still waiting for a time.
//
// Click-to-place rather than drag: at this cell size it's faster and can't be
// half-dropped, and it works the same by keyboard. Picking someone up lights
// the times that work for them and dims everything else — which is the whole
// point of the screen, since the alternative is Lori cross-referencing each
// family's answers by eye.
//
// Green is a stronger yes than gold: back-to-back with the performer's own
// first session under a different coach, or within 40 minutes of a sibling.
// That's Lori's own rule set (see the coaching draft-rules doc) surfaced as
// colour rather than left for her to remember.
//
// The board reloads from /board after every change instead of patching state
// locally. It's one small read, it can't drift, and it means a second admin's
// placements show up on the next action rather than silently conflicting.
import { apiFetch } from '../core/api.js';
import { COACHING_EVENTS } from '../core/state.js';
import { toast, toastError } from '../core/ui.js';
import { VIEWS, VIEW_INIT } from '../core/routing.js';
import { printDaySheets } from './admin-coaching-print.js';
import { openHelpSection } from './admin-help.js';

let cbEventId = null;      // which day is on screen
let cbBoard = null;        // last /board payload
let cbPicked = null;       // request id lifted from the rail
let cbLifted = null;       // assignment id lifted off the grid
let cbLocked = null;       // slot id whose confirmed-lock notice is showing
let cbUnlocked = {};       // assignment ids Lori has explicitly unlocked
let cbSort = 'asked';      // 'asked' | 'fit'

const fmtTime = hhmm => {
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')}${h >= 12 ? 'pm' : 'am'}`;
};
const hourOf = hhmm => parseInt(hhmm.slice(0, 2), 10);
// Families pick whole hours, so show them back as the range they chose
// rather than as a single start time.
const hourRange = h => {
  const short = n => `${n % 12 === 0 ? 12 : n % 12}${n >= 12 ? 'pm' : 'am'}`;
  return `${short(h)}–${short(h + 1)}`;
};

// Date order, and default to the day that's coming up next — these are
// calendar events, so "the one still ahead of me" is nearly always the one
// she means.
function boardEvents() {
  return COACHING_EVENTS.filter(e => !e.archived)
    .slice()
    .sort((a, b) => a.eventDate.localeCompare(b.eventDate));
}

function defaultEventId() {
  const events = boardEvents();
  if (!events.length) return null;
  const today = new Date().toISOString().slice(0, 10);
  return (events.find(e => e.eventDate >= today) || events[events.length - 1]).id;
}

// ── Loading ──────────────────────────────────────────────────────────

async function loadBoard(eventId) {
  cbEventId = eventId;
  try {
    cbBoard = await apiFetch('/coaching/events/' + eventId + '/board');
  } catch (e) {
    cbBoard = null;
    toastError(e.message);
  }
  redraw();
}

function redraw() {
  const main = document.getElementById('main');
  if (!main) return;
  main.innerHTML = VIEWS['a-coaching-board']();
  wireBoard();
}

// ── Derived state ────────────────────────────────────────────────────

const unplaced = () => (cbBoard.requests || []).filter(r => r.placed < r.sessions);
const assignmentBySlot = slotId => (cbBoard.assignments || []).find(a => a.slotId === slotId);
const requestById = id => (cbBoard.requests || []).find(r => r.id === id);

// Whoever is in hand: a rail chip, or someone lifted off the grid.
function subject() {
  if (cbPicked) return { request: requestById(cbPicked), assignmentId: null };
  if (cbLifted) {
    const a = (cbBoard.assignments || []).find(x => x.id === cbLifted);
    return a ? { request: requestById(a.requestId), assignmentId: a.id } : null;
  }
  return null;
}

// Times this family is already on site, split into the performer's own
// sessions and their siblings' — the two want different radii.
function kinTimes(request, ignoreAssignmentId) {
  const own = [], sib = [];
  for (const a of cbBoard.assignments || []) {
    if (a.id === ignoreAssignmentId) continue;
    if (a.accountId !== request.accountId) continue;
    const slot = slotById(a.slotId);
    if (!slot) continue;
    (a.performerId === request.performerId ? own : sib).push(slot.startsAt);
  }
  return { own, sib };
}

function slotById(slotId) {
  for (const c of cbBoard.coaches || []) {
    const s = (c.slots || []).find(x => x.id === slotId);
    if (s) return { ...s, coach: c };
  }
  return null;
}

function times() {
  const first = (cbBoard.coaches || [])[0];
  return first ? (first.slots || []).map(s => s.startsAt) : [];
}

// How many open, legal cells this request could still go in. Drives both the
// rail's "hardest to fit" order and the red flag on someone with nowhere left.
function fitCount(request) {
  let n = 0;
  for (const c of cbBoard.coaches || []) {
    if (!request.coachIds.includes(c.id)) continue;
    for (const s of c.slots || []) {
      if (s.status !== 'open' || assignmentBySlot(s.id)) continue;
      if (!request.windows.includes(hourOf(s.startsAt))) continue;
      n += 1;
    }
  }
  return n;
}

// ── Rail ─────────────────────────────────────────────────────────────

function renderRail() {
  const list = unplaced().map(r => ({ ...r, fit: fitCount(r) }));
  list.sort(cbSort === 'fit'
    ? (a, b) => a.fit - b.fit
    : (a, b) => a.createdAt.localeCompare(b.createdAt));

  const sessions = list.reduce((n, r) => n + (r.sessions - r.placed), 0);
  const coachName = id => (cbBoard.coaches.find(c => c.id === id) || {}).name || '';

  const chips = list.map(r => {
    const kin = kinTimes(r, null);
    const siblings = list.filter(o => o.accountId === r.accountId && o.id !== r.id);
    let kinNote = '';
    if (kin.own.length) kinNote = 'Their first session is at ' + fmtTime(kin.own[0]);
    else if (kin.sib.length) kinNote = 'Family already at ' + kin.sib.map(fmtTime).join(', ');
    else if (siblings.length) kinNote = 'Sibling of ' + siblings.map(o => o.performerName.split(' ')[0]).join(', ') + ' — place together';

    const fitColor = r.fit === 0 ? 'var(--red)' : r.fit <= 3 ? 'var(--red)' : r.fit <= 8 ? '#fbbf24' : 'var(--muted)';
    const fitLabel = r.fit === 0 ? 'Nowhere left to put them'
      : r.fit === 1 ? 'Only 1 time still fits'
      : r.fit + ' times still fit';
    const wanted = r.sessions - r.placed;

    return `<button data-action="cb-pick" data-request="${r.id}" style="display:block;width:100%;text-align:left;border:1px solid ${cbPicked === r.id ? 'var(--gold)' : 'var(--border-soft)'};background:${cbPicked === r.id ? 'rgba(201,168,76,0.1)' : 'var(--bg-card)'};border-radius:8px;padding:10px 12px;cursor:pointer;font-family:inherit;color:var(--cream)">
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:8px">
        <span style="font-size:14px">${esc(r.performerName)}</span>
        <span style="font-size:11px;color:var(--gold-light);white-space:nowrap">${wanted === 1 ? '1 session' : wanted + ' sessions'}</span>
      </div>
      <div style="font-size:11.5px;color:var(--muted);margin-top:3px;line-height:1.45">${r.coachIds.length === cbBoard.coaches.length ? 'Any coach' : esc(r.coachIds.map(coachName).join(' or ')) + ' only'}</div>
      <div style="font-size:11.5px;margin-top:3px;line-height:1.45">Free ${r.windows.slice().sort((a, b) => a - b).map(hourRange).join(', ')}</div>
      <div style="display:flex;justify-content:space-between;gap:8px;margin-top:3px">
        <span style="font-size:11px;color:${fitColor}">${fitLabel}</span>
        <span style="font-size:11px;color:var(--muted)">asked ${new Date(r.createdAt.replace(' ', 'T')).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
      </div>
      ${kinNote ? `<div style="font-size:11px;color:var(--green);margin-top:5px">${esc(kinNote)}</div>` : ''}
    </button>`;
  }).join('');

  const sortBtn = (mode, label) => `<button data-action="cb-sort" data-mode="${mode}" style="background:none;border:none;font-family:inherit;font-size:11.5px;cursor:pointer;padding:0;color:${cbSort === mode ? 'var(--gold-light)' : 'var(--muted)'}">${label}</button>`;

  return `<div class="card" style="width:262px;flex-shrink:0;padding:18px">
    <div class="card-hd" style="font-size:15px;margin-bottom:4px">Unplaced</div>
    <div style="font-size:12px;color:var(--muted);margin-bottom:10px">${list.length} ${list.length === 1 ? 'performer' : 'performers'}, ${sessions} ${sessions === 1 ? 'session' : 'sessions'}</div>
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:14px;font-size:11.5px;color:var(--muted)">
      <span>Order:</span>${sortBtn('asked', 'first to ask')}<span style="color:rgba(255,255,255,0.2)">·</span>${sortBtn('fit', 'hardest to fit')}
    </div>
    <div style="display:flex;flex-direction:column;gap:8px">${chips || '<p class="empty" style="padding:4px 0">Everyone has a time.</p>'}</div>
    <div style="margin-top:16px;padding-top:14px;border-top:1px solid var(--border-soft);font-size:11.5px;line-height:1.6">${railHint()}</div>
  </div>`;
}

function railHint() {
  const subj = subject();
  if (!subj) {
    return '<span style="color:var(--muted)">Click a performer to light up the times that work for them, or click anyone already on the board to move them. Whoever is left over simply isn\'t scheduled — they\'re never charged.</span>';
  }
  const kin = kinTimes(subj.request, subj.assignmentId);
  let hint = subj.assignmentId
    ? `Moving ${subj.request.performerName} — click a lit-up time, or click them again to leave them put.`
    : `${subj.request.performerName} — click any lit-up time to place them.`;
  if (kin.own.length) hint += ' Green is back-to-back with their first session, under a different coach.';
  else if (kin.sib.length) hint += ' Green sits close to the rest of the family, so it stays one trip.';
  return `<span style="color:var(--gold-light)">${esc(hint)}</span>` + (subj.assignmentId
    ? `<button data-action="cb-return" data-assignment="${subj.assignmentId}" style="display:block;margin-top:8px;background:none;border:none;padding:0;font-family:inherit;font-size:11.5px;color:var(--gold);text-decoration:underline;cursor:pointer">Put back in Unplaced</button>`
    : '');
}

// ── Grid ─────────────────────────────────────────────────────────────

const CELL_BASE = 'height:44px;border-radius:7px;display:flex;flex-direction:column;justify-content:center;padding:0 9px;font-size:12px;font-family:inherit;text-align:left;width:100%;overflow:hidden';
const CELL_STYLE = {
  open:      'background:rgba(255,255,255,0.03);border:1px solid var(--border-soft)',
  draft:     'background:rgba(201,168,76,0.07);border:1px dashed rgba(201,168,76,0.5)',
  sent:      'background:rgba(201,168,76,0.1);border:1px solid rgba(201,168,76,0.45)',
  confirmed: 'background:rgba(52,211,153,0.08);border:1px solid rgba(52,211,153,0.38)',
  break:     'background:repeating-linear-gradient(135deg,rgba(255,255,255,0.035) 0 6px,transparent 6px 12px);border:1px dotted rgba(255,255,255,0.1)',
  off:       'background:var(--bg-deep);border:1px solid rgba(255,255,255,0.05)',
  legal:     'background:rgba(201,168,76,0.13);border:1px solid rgba(201,168,76,0.5);cursor:pointer',
  best:      'background:rgba(52,211,153,0.16);border:1px solid rgba(52,211,153,0.6);cursor:pointer'
};
const STATUS_LABEL = { draft: 'Draft', sent: 'Awaiting confirm', confirmed: 'Confirmed · paid' };

function renderGrid() {
  const coaches = cbBoard.coaches || [];
  if (!coaches.length) {
    return `<div class="card" style="flex-grow:1"><p class="empty">No coaches on this day yet — add them on the event first.</p></div>`;
  }

  const subj = subject();
  const kin = subj ? kinTimes(subj.request, subj.assignmentId) : { own: [], sib: [] };
  const rowTimes = times();
  const ownIdx = kin.own.map(t => rowTimes.indexOf(t));
  const sibIdx = kin.sib.map(t => rowTimes.indexOf(t));
  const ownCoaches = subj
    ? (cbBoard.assignments || [])
        .filter(a => a.performerId === subj.request.performerId && a.id !== subj.assignmentId)
        .map(a => (slotById(a.slotId) || {}).coach)
        .filter(Boolean)
        .map(c => c.id)
    : [];

  const header = `<div style="display:flex;gap:6px;margin-bottom:8px">
    <div style="width:74px;flex-shrink:0"></div>
    ${coaches.map(c => `<div style="flex-grow:1;flex-basis:0;min-width:0;padding:0 4px 8px">
      <div style="font-size:14px">${esc(c.name)}</div>
    </div>`).join('')}
  </div>`;

  const rows = rowTimes.map((time, ti) => {
    const rowKin = kin.own.includes(time) || kin.sib.includes(time);
    const cells = coaches.map((c, ci) => {
      const isLastCoach = ci === coaches.length - 1;
      const slot = (c.slots || []).find(s => s.startsAt === time);
      if (!slot) return '<div style="flex-grow:1;flex-basis:0"></div>';
      const a = assignmentBySlot(slot.id);
      const isLifted = a && a.id === cbLifted;

      let style, label = '', meta = '', action = '', extra = '';
      if (a) {
        style = CELL_STYLE[a.status];
        label = a.performerName;
        meta = STATUS_LABEL[a.status];
      } else {
        style = CELL_STYLE[slot.status] || CELL_STYLE.open;
        if (slot.status === 'break') meta = 'Break';
      }

      // Can whoever is in hand legally land here?
      const free = !a && slot.status === 'open';
      const coachOk = subj && subj.request.coachIds.includes(c.id);
      const hourOk = subj && subj.request.windows.includes(hourOf(time));
      // Their own other session is at this time under another coach: the
      // server would refuse it, so the board never offers it.
      const selfClash = subj && (cbBoard.assignments || []).some(x =>
        x.performerId === subj.request.performerId
        && x.id !== subj.assignmentId
        && (slotById(x.slotId) || {}).startsAt === time);
      const legal = free && coachOk && hourOk && !selfClash;
      // Their own second session wants the very next slot under a coach they
      // don't already have; a sibling only needs to be nearby.
      const freshCoach = !ownCoaches.includes(c.id) || subj?.request.coachIds.length === 1;
      const best = legal && ((ownIdx.some(i => Math.abs(i - ti) === 1) && freshCoach)
        || sibIdx.some(i => Math.abs(i - ti) <= 2));

      if (subj) {
        if (isLifted) {
          style += ';opacity:0.45;border:1px dashed var(--gold)';
          action = `data-action="cb-drop"`;
        } else if (legal) {
          style = CELL_BASE_OVERRIDE(best ? CELL_STYLE.best : CELL_STYLE.legal);
          action = `data-action="cb-place" data-slot="${slot.id}"`;
        } else if (free && subj && !selfClash) {
          // Outside what the family asked for. Still reachable — a parent who
          // rings to say another time works is exactly the case Lori needs it
          // for — but it takes a confirm, and it never looks inviting.
          style += ';opacity:0.2;cursor:pointer';
          action = `data-action="cb-place-override" data-slot="${slot.id}"`;
        } else {
          style += ';opacity:0.2';
        }
      } else if (a) {
        action = a.status === 'confirmed' && !cbUnlocked[a.id]
          ? `data-action="cb-locked" data-slot="${slot.id}"`
          : `data-action="cb-lift" data-assignment="${a.id}"`;
        style += ';cursor:pointer';
      }

      if (slot.id === cbLocked) extra = ';box-shadow:0 0 0 2px rgba(52,211,153,0.5)';

      const lock = a && a.status === 'confirmed' && !cbUnlocked[a.id]
        ? '<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#34d399" stroke-width="2.5" style="flex-shrink:0"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>'
        : '';

      // The unlock notice anchors to the cell Lori clicked. Buttons can't
      // nest, so the cell is a positioned wrapper around its own button.
      const panel = slot.id === cbLocked ? `<div style="position:absolute;top:50px;${isLastCoach ? 'right:0' : 'left:0'};width:268px;background:var(--bg-card);border:1px solid rgba(52,211,153,0.45);border-radius:9px;padding:14px 16px;box-shadow:0 10px 30px rgba(0,0,0,0.55);z-index:20;text-align:left">
          <div style="font-size:13px;color:var(--cream);line-height:1.5">${esc(a.performerName)} confirmed this time and has already paid.</div>
          <div style="font-size:12px;color:var(--muted);margin-top:6px;line-height:1.5">Moving them means emailing a new time and asking them to confirm again.</div>
          <div style="display:flex;gap:8px;margin-top:12px">
            <button data-action="cb-unlock" data-assignment="${a.id}" style="padding:5px 13px;background:transparent;border:1px solid rgba(248,113,113,0.45);color:var(--red);font-size:12.5px;border-radius:7px;cursor:pointer;font-family:inherit">Unlock &amp; move</button>
            <button data-action="cb-cancel-lock" style="padding:5px 13px;background:transparent;border:1px solid rgba(255,255,255,0.12);color:var(--muted);font-size:12.5px;border-radius:7px;cursor:pointer;font-family:inherit">Leave it</button>
          </div>
        </div>` : '';

      return `<div style="flex-grow:1;flex-basis:0;min-width:0;position:relative">
        <button ${action} style="${CELL_BASE};${style}${extra}">
          <span style="color:var(--cream);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(label)}</span>
          <span style="font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:var(--muted);display:flex;align-items:center;gap:4px">${lock}${meta}</span>
        </button>${panel}
      </div>`;
    }).join('');

    return `<div style="display:flex;gap:6px;align-items:stretch">
      <div style="width:74px;flex-shrink:0;display:flex;align-items:center;justify-content:flex-end;gap:5px;padding-right:4px;font-size:12px;color:${rowKin ? 'var(--green)' : 'var(--muted)'};font-variant-numeric:tabular-nums">
        ${rowKin ? '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#34d399" stroke-width="2"><circle cx="9" cy="7" r="4"/><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/></svg>' : ''}
        <span>${fmtTime(time)}</span>
      </div>${cells}
    </div>`;
  }).join('');

  const legend = [
    ['background:rgba(201,168,76,0.07);border:1px dashed rgba(201,168,76,0.5)', 'Draft — move freely'],
    ['background:rgba(201,168,76,0.1);border:1px solid rgba(201,168,76,0.45)', 'Sent — moving means re-sending'],
    ['background:rgba(52,211,153,0.08);border:1px solid rgba(52,211,153,0.38)', 'Confirmed &amp; paid — click to unlock'],
    ['background:rgba(52,211,153,0.16);border:1px solid rgba(52,211,153,0.6)', 'Back-to-back with a second coach, or close to a sibling'],
    ['background:rgba(255,255,255,0.03);border:1px dotted rgba(255,255,255,0.1)', 'Break'],
    ['background:var(--bg-deep);border:1px solid rgba(255,255,255,0.05)', 'Coach not working']
  ].map(([style, label]) => `<span style="display:inline-flex;align-items:center;gap:6px"><span style="width:12px;height:12px;border-radius:3px;${style}"></span> ${label}</span>`).join('');

  return `<div class="card" style="flex-grow:1;min-width:0;padding:18px">
    ${header}
    <div style="display:flex;flex-direction:column;gap:6px">${rows}</div>
    <div style="display:flex;flex-wrap:wrap;gap:16px;margin-top:18px;padding-top:14px;border-top:1px solid var(--border-soft);font-size:11.5px;color:var(--muted)">${legend}</div>
  </div>`;
}

// Legal/best cells replace the base status styling rather than layering on it.
function CELL_BASE_OVERRIDE(style) {
  return style;
}

// ── View ─────────────────────────────────────────────────────────────

VIEWS['a-coaching-board'] = () => {
  const events = boardEvents();
  if (!events.length) {
    return `<div class="page-head"><h2 class="serif">Assignment <em>Board</em></h2></div>
      <div class="card"><p class="empty">No coaching events yet.</p></div>`;
  }
  if (!cbBoard) {
    return `<div class="page-head"><h2 class="serif">Assignment <em>Board</em></h2></div>
      <div class="card"><p class="empty">Loading…</p></div>`;
  }

  const ev = cbBoard.event;
  const drafts = (cbBoard.assignments || []).filter(a => a.status === 'draft').length;
  const waiting = unplaced().reduce((n, r) => n + (r.sessions - r.placed), 0);
  const switcher = events.map(e => `<button data-action="cb-switch" data-event="${e.id}" style="padding:4px 14px;background:transparent;border:1px solid ${e.id === cbEventId ? 'rgba(201,168,76,0.5)' : 'rgba(201,168,76,0.18)'};color:${e.id === cbEventId ? 'var(--gold-light)' : 'var(--gold)'};font-size:12px;border-radius:7px;cursor:pointer;font-family:inherit">${esc(e.name)} · ${new Date(e.eventDate + 'T00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</button>`).join('');

  return `
    <div class="page-head" style="display:flex;align-items:flex-end;justify-content:space-between;gap:20px;flex-wrap:wrap">
      <div>
        <span class="eyebrow">Coaching · Assignment Board</span>
        <h2 class="serif">${esc(ev.name)}</h2>
        <div class="sub">${new Date(ev.eventDate + 'T00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} · ${fmtTime(ev.startsAt)}–${fmtTime(ev.endsAt)} · ${ev.slotMinutes}-minute sessions · ${fmt$(ev.price)} each</div>
      </div>
      <div style="display:flex;gap:8px;align-items:center">
        <span class="pill pill-off">${drafts} ${drafts === 1 ? 'draft' : 'drafts'} not sent</span>
        ${waiting ? `<button class="btn-ghost" data-action="cb-autodraft">Fill in a first pass</button>
        <button data-action="cb-rules" title="What the first pass does" style="background:none;border:none;padding:0 2px;font-family:inherit;font-size:12px;color:var(--muted);cursor:pointer;text-decoration:underline">rules</button>` : ''}
        <button class="btn-ghost" data-action="cb-print">Print day sheets</button>
        ${drafts ? `<button class="btn" style="width:auto;padding:9px 18px" data-action="cb-send">Send ${drafts} ${drafts === 1 ? 'draft' : 'drafts'}</button>` : ''}
      </div>
    </div>
    <div style="display:flex;gap:8px;margin-bottom:16px;flex-wrap:wrap">${switcher}</div>
    <div style="display:flex;gap:18px;align-items:flex-start">${renderRail()}${renderGrid()}</div>`;
};

VIEW_INIT['a-coaching-board'] = () => {
  const id = cbEventId || defaultEventId();
  if (!id) return;
  if (!cbBoard || cbBoard.event.id !== id) loadBoard(id);
  else wireBoard();
};

function wireBoard() {
  const main = document.getElementById('main');
  if (!main) return;
  const on = (action, fn) => main.querySelectorAll(`[data-action="${action}"]`).forEach(el => {
    el.addEventListener('click', () => fn(el));
  });

  on('cb-switch', el => {
    resetSelection();
    loadBoard(Number(el.dataset.event));
  });
  on('cb-sort', el => { cbSort = el.dataset.mode; redraw(); });
  on('cb-pick', el => {
    const id = Number(el.dataset.request);
    cbPicked = cbPicked === id ? null : id;
    cbLifted = null; cbLocked = null;
    redraw();
  });
  on('cb-lift', el => {
    cbLifted = Number(el.dataset.assignment);
    cbPicked = null; cbLocked = null;
    redraw();
  });
  on('cb-drop', () => { cbLifted = null; redraw(); });
  on('cb-locked', el => {
    cbLocked = Number(el.dataset.slot);
    cbPicked = null; cbLifted = null;
    redraw();
  });
  on('cb-cancel-lock', () => { cbLocked = null; redraw(); });
  on('cb-unlock', el => {
    cbUnlocked[Number(el.dataset.assignment)] = true;
    cbLifted = Number(el.dataset.assignment);
    cbLocked = null;
    redraw();
  });
  on('cb-place', el => place(Number(el.dataset.slot), false));
  on('cb-place-override', el => {
    const subj = subject();
    if (!subj) return;
    const slot = slotById(Number(el.dataset.slot));
    if (!confirm(`${slot.coach.name} at ${fmtTime(slot.startsAt)} isn't a coach or hour ${subj.request.performerName}'s family asked for. Place them there anyway?`)) return;
    place(Number(el.dataset.slot), true);
  });
  on('cb-return', el => returnToRail(Number(el.dataset.assignment)));
  on('cb-print', () => printDaySheets(cbEventId));
  on('cb-rules', () => openHelpSection('coaching', 'boundaries'));
  on('cb-autodraft', async () => {
    const waiting = unplaced().reduce((n, r) => n + (r.sessions - r.placed), 0);
    if (!confirm(`Fit as many of the ${waiting} waiting ${waiting === 1 ? 'session' : 'sessions'} as it can, working through families in the order they asked?\n\nNothing already on the board moves, and nothing is emailed. Everything it places is a draft you can move, clear, or ignore.`)) return;
    try {
      const res = await apiFetch('/coaching/events/' + cbEventId + '/autodraft', { method: 'POST', body: JSON.stringify({}) });
      resetSelection();
      await loadBoard(cbEventId);
      const left = res.unplaced + res.partial;
      toast(res.placed
        ? `Placed ${res.placed} ${res.placed === 1 ? 'session' : 'sessions'}` + (left ? ` · ${left} still to sort out` : '')
        : 'Nothing else would fit — the rest need a hand');
    } catch (e) {
      toastError(e.message);
    }
  });
  on('cb-send', async () => {
    const n = (cbBoard.assignments || []).filter(a => a.status === 'draft').length;
    if (!confirm(`Email ${n} ${n === 1 ? 'time' : 'times'} to the families they belong to? Each family gets one email with everything set aside for them.`)) return;
    try {
      const res = await apiFetch('/coaching/send', { method: 'POST', body: JSON.stringify({ eventId: cbEventId }) });
      await loadBoard(cbEventId);
      toast(`Sent to ${res.families} ${res.families === 1 ? 'family' : 'families'}`);
    } catch (e) {
      toastError(e.message);
    }
  });
}

function resetSelection() {
  cbPicked = null; cbLifted = null; cbLocked = null; cbUnlocked = {};
}

async function place(slotId, override) {
  const subj = subject();
  if (!subj) return;
  try {
    if (subj.assignmentId) {
      await apiFetch('/coaching/assignments/' + subj.assignmentId, {
        method: 'PUT',
        body: JSON.stringify({ slotId, override, unlock: !!cbUnlocked[subj.assignmentId] })
      });
    } else {
      await apiFetch('/coaching/assignments', {
        method: 'POST',
        body: JSON.stringify({ requestId: subj.request.id, slotId, override })
      });
    }
    const name = subj.request.performerName;
    const keepPicked = !subj.assignmentId && subj.request.sessions - subj.request.placed > 1;
    resetSelection();
    if (keepPicked) cbPicked = subj.request.id;
    await loadBoard(cbEventId);
    toast(name + ' placed');
  } catch (e) {
    toastError(e.message);
  }
}

async function returnToRail(assignmentId) {
  try {
    await apiFetch('/coaching/assignments/' + assignmentId, { method: 'DELETE' });
    resetSelection();
    await loadBoard(cbEventId);
    toast('Back in Unplaced');
  } catch (e) {
    toastError(e.message);
  }
}
